/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { heavy, semiBold, colors, type Color } from '@/styles';
import { type StyledComponent } from '@/helpers';

export type ProgressCircleProps = StyledComponent & {
  progress: number;
  textColor?: Color;
  strokeColor?: Color;
  backgroundColor?: Color;
  strokeModifier?: number;
  showBackground?: boolean;
  text?: string;
  isRebrand?: boolean;
};

/**
 * Progress circle component
 */
function ProgressCircle({
  backgroundColor = colors.silverSand,
  className = '',
  isRebrand = false,
  progress,
  showBackground = false,
  strokeColor = colors.boulder,
  strokeModifier = 0.03,
  text = '',
  textColor = colors.boulder,
}: ProgressCircleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [circleState, setCircleState] = useState({ circumference: 0, size: 0, radius: 0 });

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const size =
      containerRef.current.clientHeight > containerRef.current.clientWidth
        ? containerRef.current.clientWidth
        : containerRef.current.clientHeight;
    const radius = (size / 2) * 0.8;
    const circumference = radius * 2 * Math.PI;

    setCircleState({
      size,
      radius,
      circumference,
    });
  }, [containerRef]);

  const circleStyles = {
    strokeDashoffset: circleState.circumference - (progress / 100) * circleState.circumference,
    transform: 'rotate(-90deg)',
  };
  const backgroundCircleStyles = {
    transform: 'rotate(-90deg)',
  };

  const styles = {
    bottom: `${(circleState.size * 1.5) / 2 / 16}rem`,
    fontSize: `${(circleState.size * 0.1) / 16}rem`,
    color: textColor,
  };

  return (
    <div
      role="progressbar"
      className={className}
      ref={containerRef}
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {circleState.circumference && (
        <svg className="foreground" height={circleState.size} width={circleState.size}>
          {showBackground && (
            <circle
              stroke={backgroundColor}
              fill="transparent"
              strokeWidth={circleState.size * strokeModifier}
              strokeDasharray={`${circleState.circumference} ${circleState.circumference}`}
              style={backgroundCircleStyles}
              r={circleState.radius}
              cx={circleState.size / 2}
              cy={circleState.size / 2}
            />
          )}
          <circle
            stroke={strokeColor}
            fill="transparent"
            strokeWidth={circleState.size * strokeModifier}
            strokeDasharray={`${circleState.circumference} ${circleState.circumference}`}
            style={circleStyles}
            r={circleState.radius}
            cx={circleState.size / 2}
            cy={circleState.size / 2}
          />
        </svg>
      )}
      {text && (
        <div className="text" style={styles}>
          {text}
        </div>
      )}
    </div>
  );
}

const styledProgressCircle = styled(ProgressCircle)`
  width: 100%;
  height: 100%;
  display: block;

  svg {
    margin: 0 auto;
    display: block;

    circle {
      transition: stroke-dashoffset 0.35s;
      transform-origin: 50% 50%;
    }
  }

  .text {
    ${({ isRebrand = false }) => (isRebrand ? semiBold() : heavy())}
    position: relative;
    z-index: 1;
    width: 80%;
    white-space: normal;
    text-align: center;
    margin: 0 auto;
  }
`;

export default styledProgressCircle;
